import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CreditCard, Save, Eye, EyeOff, Copy, Webhook, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StripePage() {
  const [config, setConfig] = useState({ secret_key: '', publishable_key: '', webhook_secret: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [secretsRevealed, setSecretsRevealed] = useState(false);

  // Fetch the unmasked secrets from the backend (which masks them by default).
  // Updates both secret_key and webhook_secret in one round trip. Idempotent.
  const ensureSecretsRevealed = async () => {
    if (secretsRevealed) return true;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/stripe/config?unmask=true`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('reveal failed');
      const data = await res.json();
      setConfig((c) => ({
        ...c,
        secret_key: data.secret_key || '',
        webhook_secret: data.webhook_secret || '',
      }));
      setSecretsRevealed(true);
      return true;
    } catch {
      toast.error('Erro ao revelar os segredos');
      return false;
    }
  };

  const handleToggleSecret = async () => {
    if (showSecret) return setShowSecret(false);
    if (await ensureSecretsRevealed()) setShowSecret(true);
  };

  const handleToggleWebhookSecret = async () => {
    if (showWebhookSecret) return setShowWebhookSecret(false);
    if (await ensureSecretsRevealed()) setShowWebhookSecret(true);
  };

  const webhookUrl = `${API_URL}/api/stripe/webhook`;

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success('URL copiada!');
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/stripe/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setConfig(data);
      // Reset reveal flag — fetched values are masked again
      setSecretsRevealed(false);
      setShowSecret(false);
      setShowWebhookSecret(false);
    } catch (error) {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/stripe/config`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        toast.success('Configuração salva com sucesso');
        fetchConfig();
      } else {
        toast.error('Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stripe-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Configuração Stripe</h1>
        <p className="text-muted-foreground">
          Gerencie as configurações da integração com o Stripe.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Integração Stripe
          </CardTitle>
          <CardDescription>
            Pagamentos, assinaturas e webhooks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1 pb-2 border-b">
              <div className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                <h3 className="font-semibold text-base">Keys do Stripe</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Configure as chaves da API do Stripe para habilitar pagamentos e assinaturas.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="publishable_key">Publishable Key</Label>
              <Input
                id="publishable_key"
                value={config.publishable_key}
                onChange={(e) => setConfig({ ...config, publishable_key: e.target.value })}
                placeholder="pk_test_..."
                data-testid="stripe-publishable-key"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="secret_key">Secret Key</Label>
              <div className="relative">
                <Input
                  id="secret_key"
                  type={showSecret ? 'text' : 'password'}
                  value={config.secret_key}
                  onChange={(e) => setConfig({ ...config, secret_key: e.target.value })}
                  placeholder="sk_test_..."
                  data-testid="stripe-secret-key"
                />
                <button
                  type="button"
                  onClick={handleToggleSecret}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Encontre em: Stripe Dashboard &gt; Developers &gt; API Keys
              </p>
            </div>

            <div className="border-t pt-4 mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <Webhook className="w-5 h-5" />
                <h3 className="font-semibold">Webhook do Stripe</h3>
              </div>
              <p className="text-[14px] text-muted-foreground">
                Configure no Stripe Dashboard para sincronizar automaticamente mudanças de plano e cancelamentos.
              </p>

              <div className="space-y-2">
                <Label>URL do Webhook (cole no Stripe Dashboard)</Label>
                <div className="flex gap-2">
                  <Input value={webhookUrl} readOnly className="font-mono text-xs" data-testid="stripe-webhook-url" />
                  <Button type="button" variant="outline" size="icon" onClick={handleCopyWebhookUrl} data-testid="stripe-webhook-copy">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  No Stripe: <strong>Developers → Webhooks → Add endpoint</strong>. Eventos a selecionar:{' '}
                  <code className="bg-secondary px-1 rounded">customer.subscription.created</code>,{' '}
                  <code className="bg-secondary px-1 rounded">customer.subscription.updated</code>,{' '}
                  <code className="bg-secondary px-1 rounded">customer.subscription.deleted</code>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhook_secret">Webhook Signing Secret</Label>
                <div className="relative">
                  <Input
                    id="webhook_secret"
                    type={showWebhookSecret ? 'text' : 'password'}
                    value={config.webhook_secret || ''}
                    onChange={(e) => setConfig({ ...config, webhook_secret: e.target.value })}
                    placeholder="whsec_..."
                    className="pr-10 font-mono text-xs"
                    data-testid="stripe-webhook-secret"
                  />
                  <button
                    type="button"
                    onClick={handleToggleWebhookSecret}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Após criar o endpoint no Stripe, copie o <strong>Signing secret</strong> (whsec_...) e cole aqui.
                </p>
              </div>
            </div>

            <Button type="submit" disabled={saving} data-testid="stripe-save-button">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
